import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useOAuthFlow } from '@/hooks/useOAuthFlow';
import { SOCIAL_PLATFORMS } from '@/lib/social-platforms';
import { OAUTH_PLATFORMS, type OAuthPlatform } from '@/lib/utils';
import { BlueskyConnectForm } from './BlueskyConnectForm';

interface PlatformConnectionButtonsProps {
  connectedPlatforms?: string[];
  onConnectionSuccess?: () => void;
  className?: string;
}

export const PlatformConnectionButtons = ({
  connectedPlatforms = [],
  onConnectionSuccess,
  className = "",
}: PlatformConnectionButtonsProps) => {
  const [showBluesky, setShowBluesky] = useState(false);

  const { connectPlatform, isConnecting } = useOAuthFlow({
    onSuccess: () => onConnectionSuccess?.(),
  });

  const connectablePlatforms = SOCIAL_PLATFORMS.filter(p => p.isConnectable);

  return (
    <>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 ${className}`}>
        {connectablePlatforms.map((platform) => {
          const isConnected = connectedPlatforms.includes(platform.id);
          const isLoading = isConnecting[platform.id];
          const Icon = platform.icon;

          return (
            <div key={platform.id} className="border border-border rounded-lg p-3 sm:p-4 hover:bg-muted/50 transition-colors font-sans">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-foreground text-sm sm:text-base truncate">{platform.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{platform.description}</p>
                  </div>
                </div>

                {isConnected && (
                  <Badge variant="secondary" className="text-xs shrink-0 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    Connected
                  </Badge>
                )}
              </div>

              <Button
                variant="default"
                className="w-full text-xs sm:text-sm font-sans"
                size="sm"
                onClick={() => {
                  if (platform.id === 'bluesky') {
                    setShowBluesky(true);
                  } else if (platform.id in OAUTH_PLATFORMS) {
                    const p = OAUTH_PLATFORMS[platform.id as keyof typeof OAUTH_PLATFORMS];
                    if (p !== 'bluesky') connectPlatform(p);
                  }
                }}
                disabled={isLoading}
              >
                {isLoading ? "Connecting..." : `Connect ${platform.name}`}
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={showBluesky} onOpenChange={setShowBluesky}>
        <DialogContent className="p-0 border-0 bg-transparent shadow-none max-w-md">
          <BlueskyConnectForm
            onSuccess={() => {
              setShowBluesky(false);
              onConnectionSuccess?.();
            }}
            onCancel={() => setShowBluesky(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
